import { z } from 'zod';

/**
 * `GET /api/<service>/overview` — what a service says about ITSELF for the fleet console
 * (COMMAND-2 Phase A / CC-3).
 *
 * ## Why this exists
 *
 * The Command Center used to hand-write a branch per service to build its tile: one for the
 * idea-engine, one for the agent-runner, one for observability, a generic fallback for the rest.
 * Two things followed. Services without a bespoke branch showed only `Reachable`/`Storage` — not
 * because the data was missing but because nobody had written them one. And **adding a service
 * meant editing the Command Center**, which quietly inverts ADR 0001: the console imports no
 * service, yet it encoded every service's shape by hand.
 *
 * This is that knowledge, moved to where it belongs. A service describes itself; the console
 * renders whatever it is given. A new service lights up its tile by publishing this — with no
 * change to the Command Center at all.
 *
 * ## Deliberately dumb
 *
 * `vitals` is label/value STRINGS, not typed domain fields. The console must not learn what a
 * "queue depth" or an "evaluation" is — that is exactly the coupling being removed. The service
 * owns both the wording and the formatting, because the service is the only thing that knows
 * what its numbers mean. Anything richer than a tile belongs in that service's own console, or
 * (for a conversational answer) behind its MCP server — not in this shape.
 *
 * ## Strict about promises, generous about extras
 *
 * The console fans out to every service and must degrade PER SOURCE. So the schema is
 * `passthrough`: a service running a newer build than the console cannot break the fleet
 * snapshot by adding a field. What it may not do is omit the headline or push unbounded content
 * — those are rejected, and {@link parseServiceOverview} turns any rejection into `null` so one
 * bad service costs its own tile and nothing else.
 */

/** One rendered row on a service's tile. Strings on purpose — see the note above. */
export const ServiceVitalSchema = z.object({
  /** Short human label, e.g. `Ideas evaluated`. */
  label: z.string().min(1).max(40),
  /** Pre-formatted value, e.g. `128`, `4.2s`, `—`. The SERVICE owns the formatting. */
  value: z.string().min(1).max(40),
});
export type ServiceVital = z.infer<typeof ServiceVitalSchema>;

export const ServiceOverviewSchema = z
  .object({
    /** The one-line headline the tile leads with, e.g. `3 awaiting review`, `idle`. */
    vital: z.string().min(1).max(80),
    /** The detail rows, in render order. Bounded so one service cannot flood the console. */
    vitals: z.array(ServiceVitalSchema).max(12),
    /**
     * `true` ⇒ this service wants a human. OPTIONAL and meaningfully so: absent means the
     * service did not say, which the console must be able to tell apart from an explicit
     * "nothing needed". Never inferred from the other fields.
     */
    attention: z.boolean().optional(),
    /**
     * How real the data behind this overview is, in the console's own vocabulary. Omitted ⇒ the
     * console keeps whatever it already determined from the health probe, so a service that does
     * not self-report cannot accidentally claim to be LIVE.
     */
    mode: z.enum(['LIVE', 'PARTIAL', 'SIM']).optional(),
  })
  .passthrough();
export type ServiceOverview = z.infer<typeof ServiceOverviewSchema>;

/** The `{data,error,meta}` envelope services already answer in (parsed loosely — we only need `data`). */
const EnvelopeShape = z.object({ data: z.unknown().nullable(), error: z.unknown().nullable().optional() });

/**
 * Read an overview from whatever a service actually returned, and NEVER throw.
 *
 * `gatherFleet` fans out to every service at once; a single malformed payload must cost that one
 * tile, not the snapshot. So this is nullable-by-design rather than a throwing parse: unusable
 * input — wrong shape, an error envelope, a service that answered nothing — is `null`, and the
 * console falls back to what the health probe alone told it.
 *
 * Accepts the bare overview or the enveloped one, so a caller need not know which it was handed.
 */
export function parseServiceOverview(input: unknown): ServiceOverview | null {
  const enveloped = EnvelopeShape.safeParse(input);
  // An error envelope is a service reporting its own failure — reading `data` anyway would be
  // treating a stated failure as a result.
  if (enveloped.success && enveloped.data.error != null) return null;
  const candidate = enveloped.success && enveloped.data.data != null ? enveloped.data.data : input;
  const parsed = ServiceOverviewSchema.safeParse(candidate);
  return parsed.success ? parsed.data : null;
}
